const TAG = "[DataDog]";

/**
 * Get the single DataDog instance (initialized via CDN in datadog-init.js)
 */
function getDD() {
    return window.DD_LOGS;
}

/**
 * Get the DataDog logger
 */
function getLogger() {
    return getDD()?.logger;
}

/**
 * Initialize DataDog - no-op since CDN handles initialization via datadog-init.js
 * Kept for API compatibility with Kotlin calls
 */
export function initDatadog(clientToken, site, service, env, version) {
    // DataDog is initialized via CDN in datadog-init.js
    // This function exists for API compatibility but does nothing
    if (getDD()) {
        console.log(TAG, "Using CDN-initialized instance");
    } else {
        console.warn(TAG, "DD_LOGS not available - datadog-init.js may not have loaded");
    }
}

/**
 * Set user information for DataDog
 * @param {string} id - User ID
 * @param {string|null} name - User name
 * @param {string|null} email - User email
 * @param {Object} extraInfo - Additional user info
 */
export function setDatadogUser(id, name, email, extraInfo) {
    const dd = getDD();
    if (!dd) {
        console.warn(TAG, "Cannot set user, DD_LOGS not available");
        return;
    }
    try {
        dd.setUser({
            id: id,
            name: name || undefined,
            email: email || undefined,
            ...extraInfo,
        });
    } catch (error) {
        console.error(TAG, "Failed to set user:", error);
    }
}

/**
 * Clear user information
 */
export function clearDatadogUser() {
    const dd = getDD();
    if (!dd) return;
    try {
        dd.clearUser();
    } catch (error) {
        console.error(TAG, "Failed to clear user:", error);
    }
}

/**
 * Log a debug message
 * @param {string} message - Log message
 * @param {Object|null} context - Additional context
 */
export function logDebug(message, context) {
    const logger = getLogger();
    if (!logger) {
        console.debug(message, context);
        return;
    }
    try {
        logger.debug(message, context || {});
    } catch (error) {
        console.error(TAG, "Failed to log debug:", error);
    }
}

/**
 * Log an info message
 * @param {string} message - Log message
 * @param {Object|null} context - Additional context
 */
export function logInfo(message, context) {
    const logger = getLogger();
    if (!logger) {
        console.info(message, context);
        return;
    }
    try {
        logger.info(message, context || {});
    } catch (error) {
        console.error(TAG, "Failed to log info:", error);
    }
}

/**
 * Log a warning message
 * @param {string} message - Log message
 * @param {Object|null} context - Additional context
 */
export function logWarn(message, context) {
    const logger = getLogger();
    if (!logger) {
        console.warn(message, context);
        return;
    }
    try {
        logger.warn(message, context || {});
    } catch (error) {
        console.error(TAG, "Failed to log warn:", error);
    }
}

/**
 * Log an error message
 * @param {string} message - Log message
 * @param {Object|null} context - Additional context
 * @param {Error|null} error - Error object
 */
export function logError(message, context, error) {
    const logger = getLogger();
    const enrichedContext = {...context};
    if (error) {
        enrichedContext.error = {
            message: error.message,
            stack: error.stack,
        };
    }
    if (!logger) {
        console.error(message, enrichedContext);
        return;
    }
    try {
        logger.error(message, enrichedContext);
    } catch (err) {
        console.error(TAG, "Failed to log error:", err);
    }
}
